import { AlertTriangle } from "lucide-react";

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const SEVERITY_STYLES = {
  red:    { bg: "bg-red-50",    border: "border-red-300",    text: "text-red-700",    dot: "bg-red-500",    label: "Red",    icon: "!" },
  yellow: { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-700",  dot: "bg-amber-500",  label: "Yellow", icon: "!" },
  green:  { bg: "bg-emerald-50",border: "border-emerald-300",text: "text-emerald-700",dot: "bg-emerald-500",label: "Green",  icon: "•" },
};

/**
 * Loan maturity / IO→P&I transition alerts panel. Shows 3 severity tiles
 * (red ≤30d, yellow ≤60d, green ≤90d) + a sorted action queue of every
 * upcoming event. Renders nothing when there are no alerts.
 */
export default function MaturityAlertPanel({ alerts }) {
  if (!alerts || alerts.total === 0) return null;
  const { counts } = alerts;
  const cards = [
    { sev: "red", label: "Critical — due ≤ 30 days", count: counts.red, note: "Immediate action required" },
    { sev: "yellow", label: "Warning — due ≤ 60 days", count: counts.yellow, note: "Begin refinance outreach" },
    { sev: "green", label: "Watchlist — due ≤ 90 days", count: counts.green, note: "Monitor & plan" },
  ];
  return (
    <div className="mb-6" data-testid="maturity-alerts-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h2 className="overline text-slate-700">Maturity & Transition Alerts</h2>
        <span className="text-xs text-slate-400 font-mono">({alerts.total} total in next 90 days)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {cards.map((c) => {
          const s = SEVERITY_STYLES[c.sev];
          const emphasise = c.count > 0;
          return (
            <div
              key={c.sev}
              data-testid={`alert-card-${c.sev}`}
              className={`rounded-sm border p-4 ${emphasise ? `${s.bg} ${s.border}` : "bg-white border-slate-200"}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                <span className={`overline ${emphasise ? s.text : "text-slate-500"}`}>{c.label}</span>
              </div>
              <div className={`mt-3 text-3xl font-mono font-bold ${emphasise ? s.text : "text-slate-400"}`}>{c.count}</div>
              <div className={`text-xs mt-1 ${emphasise ? s.text : "text-slate-400"}`}>{c.note}</div>
            </div>
          );
        })}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Action Queue</div>
          <div className="text-xs text-slate-500">Sorted by urgency</div>
        </div>
        <ul className="divide-y divide-slate-100">
          {alerts.alerts.map((a, idx) => {
            const s = SEVERITY_STYLES[a.severity];
            return (
              <li
                key={`${a.loan_id}-${a.alert_type}-${idx}`}
                data-testid={`alert-row-${a.loan_id}-${a.alert_type}`}
                className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${s.dot} shrink-0`}></span>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">
                      <span className="font-mono font-bold">{a.loan_number}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      {a.alert_type === "maturity" ? "Maturity" : "IO → P&I transition"}
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="text-slate-500">{a.property_address}</span>
                      {a.borrower_name && <span className="text-slate-400 text-xs ml-2">({a.borrower_name})</span>}
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">
                      Event {a.event_date}
                      {a.amount_due > 0 && ` · Amount due ${currency(a.amount_due)}`}
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${s.border} ${s.bg} ${s.text} text-[11px] font-bold font-mono shrink-0`}>
                  {a.days_until}d · {s.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
